import * as React from 'react';
import { ChangeEvent, SyntheticEvent, useEffect, useMemo, useState, useReducer } from 'react';
import useCollapse from 'react-collapsed';
import { UseCollapseOutput } from 'react-collapsed/dist/types';
import { TransProps, useTranslation } from 'react-i18next';

import { AddPhotoTheme, Color, DirectionEnum, IconName, ValidationBarTheme } from '../../enums';
import { AddPhotoFile } from '../../interfaces';
import { UseState } from '../../types';
import { Icon } from '../icon/icon.component';

import { setFileProgressValuesAction } from './addPhoto.actions';
import { reducerInitialState } from './addPhoto.const';
import { addPhotoReducer } from './addPhoto.reducer';
import {
  AddPhotoBar,
  AddPhotoBarLabel,
  AddPhotoBox,
  AddPhotoIconContainer,
  ArrowButton,
  ArrowsContainer,
  AvatarContainer,
  AvatarImage,
  ButtonsContainer,
  Container,
  Dot,
  FileCell,
  FileInput,
  FileLabel,
  FileRemoveButton,
  FileRow,
  FileRowContainer,
  FilesList,
  PhotoBoxHeader,
  PhotoBoxSubheader,
  PhotoPreview,
  PhotoPreviewContainer,
  ProgressBar,
  ProgressBarContainer,
  Requirement,
  RequirementLabel,
  RequirementsBox,
  RowButtonsContainer,
  SizeErrorMessage,
  SizeErrorMessageContainer,
  SmallAddPhoto,
  SmallContainer,
  SmallProgressBarContainer,
  StyledValidationBar
} from './addPhoto.styled';
import { AddPhotoActions, AddPhotoProps, AddPhotoState } from './addPhoto.types';

export const AddPhoto: React.FC<AddPhotoProps> = (props: AddPhotoProps): JSX.Element => {
  const { t }: TransProps<never> = useTranslation();
  const {
    addPhotoTheme = AddPhotoTheme.WithBarLoaderOnInit,
    className,
    requirements = [],
    files = [],
    addFile,
    onFilesListChange,
    removeFile,
    acceptedFilesTypes = 'image/png, image/jpeg',
    maxFileSizeInBytes,
    maxNumberOfFiles = 100,
    validationMessage,
  }: AddPhotoProps = props;
  const [filesArray, setFilesArray]: UseState<AddPhotoFile[]> = useState<AddPhotoFile[]>(files);
  const [photoForPreview, setPhotoForPreview]: UseState<string | null> = useState<string | null>(null);
  const [isFirstHover, setIsFirstHover]: UseState<boolean> = useState<boolean>(!!files[0]);
  const { getCollapseProps, setExpanded }: UseCollapseOutput = useCollapse();
  const [addPhotoState, addPhotoDispatch]: [AddPhotoState, React.Dispatch<AddPhotoActions>] = useReducer(
    addPhotoReducer,
    reducerInitialState
  );
  const [isInputFileDisabled, setIsInputFileDisabled]: UseState<boolean> = useState<boolean>(false);

  useEffect(
    (): void => {
      if (files.length) {
        setFilesArray(files);
        setIsFirstHover(!!files[0]);
      } else if (!files.length && filesArray.length) {
        setFilesArray(files);
        setIsFirstHover(false);
        setPhotoForPreview(null);
      }
    },
    [files.length ? files : JSON.stringify(files)]
  );

  const moveFileAction: (
    fileIndex: number, direction: DirectionEnum
  ) => void = (fileIndex: number, direction: DirectionEnum): void => {
    const filesArrayClone: AddPhotoFile[] = [...filesArray];
    const fileCopy: AddPhotoFile = filesArrayClone.splice(fileIndex, 1)[0];
    filesArrayClone.splice(direction === DirectionEnum.Up ? (fileIndex - 1) : (fileIndex + 1), 0, fileCopy);
    setFilesArray(filesArrayClone);
    setPhotoForPreview(fileCopy.path);
    if (onFilesListChange) {
      onFilesListChange(filesArrayClone);
    }
  };

  const removeFileAction: (fileId: string, e: SyntheticEvent) => void = (fileId: string, e: SyntheticEvent): void => {
    e.preventDefault();
    if (removeFile) {
      removeFile(fileId);
    }
    const filesArrayClone: AddPhotoFile[] = [...filesArray];
    const filteredArray: AddPhotoFile[] = filesArrayClone.filter((file: AddPhotoFile) => file.id !== fileId);
    setFilesArray(filteredArray);
    setPhotoForPreview(filteredArray.length ? filteredArray[0].path : null);
    if (onFilesListChange) {
      onFilesListChange(filteredArray);
    }
  };

  const onFileChange: (event: ChangeEvent<HTMLInputElement>) => void = (event: ChangeEvent<HTMLInputElement>): void => {
    const promises: Promise<void>[] = [];
    const inputFiles: File[] = Array.from(event.target.files ?? []);
    const filesArrayClone: AddPhotoFile[] = [...filesArray];
    const newAddFilesArray: AddPhotoFile[] = [...filesArray];
    inputFiles.forEach((file: File, index: number): void => {
      if (maxFileSizeInBytes !== undefined && file.size > maxFileSizeInBytes) {
        setExpanded(true);
        return;
      }

      if (index === 0) {
        setIsInputFileDisabled(true);
      }

      promises.push(new Promise((resolve: (() => void)) => {
        const reader: FileReader = new FileReader();
        const progressEventListener: (e: ProgressEvent<FileReader>) => void = (e: ProgressEvent<FileReader>): void => {
          const percentLoaded: number = Math.round((e.loaded / e.total) * 100);
          const element: AddPhotoFile | undefined = filesArrayClone.find((item: AddPhotoFile): boolean => item.id === file.name);
          if (addPhotoTheme === AddPhotoTheme.SmallWithAvatar) {
            filesArrayClone[0] = {
              id: file.name,
              path: '',
              name: '',
              type: '',
              objectName: null
            };
          } else if (!element) {
            filesArrayClone.push({
              id: file.name,
              path: '',
              name: '',
              type: '',
              objectName: null
            });
          }
          setFileProgressValuesAction(addPhotoDispatch, file.name, percentLoaded);
          setFilesArray(filesArrayClone);
        };
        const endEventListener: (e: ProgressEvent<FileReader>, createdFile?: AddPhotoFile) => void = (
          e: ProgressEvent<FileReader>, createdFile?: AddPhotoFile
        ): void => {
          reader.removeEventListener('progress', progressEventListener);
          reader.removeEventListener('loadend', progressEventListener);
          if (createdFile) {
            if (addPhotoTheme === AddPhotoTheme.SmallWithAvatar) {
              newAddFilesArray[0] = createdFile;
            } else {
              newAddFilesArray.push(createdFile);
            }
            if (e.loaded === e.total) {
              setFileProgressValuesAction(addPhotoDispatch, file.name, 100);
            }
            if (onFilesListChange) {
              onFilesListChange(newAddFilesArray);
            }
            setTimeout((): void => resolve(), 500);
          } else {
            const newFilesArray: AddPhotoFile[] = filesArrayClone
              .map((item: AddPhotoFile): AddPhotoFile => {
                if (item.id === file.name && e.loaded === e.total) {
                  item.path = e.target?.result?.toString() ?? '';
                  setFileProgressValuesAction(addPhotoDispatch, file.name, 100);
                }

                return item;
              });
            setFilesArray(newFilesArray);
            if (onFilesListChange) {
              onFilesListChange(newFilesArray);
            }
            setTimeout((): void => resolve(), 500);
          }
        };
        if (addFile) {
          void addFile(file, progressEventListener).then((createdFile: AddPhotoFile): void => {
            reader.addEventListener('loadend', (e: ProgressEvent<FileReader>): void => endEventListener(e, createdFile));
            reader.readAsDataURL(file);
          });
        } else {
          reader.addEventListener('progress', progressEventListener);
          reader.addEventListener('loadend', endEventListener);
          reader.readAsDataURL(file);
        }
      }));
    });
    void Promise.all(promises).then((): void => {
      if (addFile) {
        setFilesArray(newAddFilesArray);
      } else {
        filesArrayClone.forEach((item: AddPhotoFile): void => {
          setFileProgressValuesAction(addPhotoDispatch, item.id, undefined);
        });
      }
      setIsInputFileDisabled(false); 
    });
  };

  const hideSizeErrorMessage = (): void => setExpanded(false);

  const maxFilesByTheme: number = useMemo(
    (): number => {
      return addPhotoTheme === AddPhotoTheme.SmallWithAvatar ? 1 : maxNumberOfFiles;
    },
    [addPhotoTheme]
  );

  return (
    <Container className={className}>
      {addPhotoTheme === AddPhotoTheme.SmallWithAvatar && (
        <SmallContainer $withFile={!!filesArray.length}>
          <SmallAddPhoto>
            <AvatarContainer>
              {(filesArray.length && filesArray[0].path) ? (
                <AvatarImage src={filesArray[0].path} />
              ) : (
                <Icon name={IconName.Collector} size={42} color={Color.Error500} />
              )}
            </AvatarContainer>
          </SmallAddPhoto>
          <SmallProgressBarContainer>
            <ProgressBar $width={`${filesArray[0] ? addPhotoState.fileProgressValues[filesArray[0].id] ?? 0 : 0}%`} />
          </SmallProgressBarContainer>
        </SmallContainer>
      )}
      {!filesArray.length && addPhotoTheme === AddPhotoTheme.WithBoxLoaderOnInit && (
        <AddPhotoBox onClick={hideSizeErrorMessage}>
          <AddPhotoIconContainer>
            <Icon name={IconName.Add} size={24} />
          </AddPhotoIconContainer>
          <PhotoBoxHeader>{t('onarte.ui.addPhoto.addPhotosHeader')}</PhotoBoxHeader>
          <PhotoBoxSubheader>{t('onarte.ui.addPhoto.addPhotosDescription')}</PhotoBoxSubheader>
          <FileInput
            type='file'
            accept={acceptedFilesTypes}
            onChange={onFileChange}
            multiple
          />
        </AddPhotoBox>
      )}
      {addPhotoTheme !== AddPhotoTheme.SmallWithAvatar && (
        <PhotoPreviewContainer $visible={!!photoForPreview}>
          {photoForPreview && <PhotoPreview src={photoForPreview} />}
        </PhotoPreviewContainer>
      )}
      {!!filesArray.length && addPhotoTheme !== AddPhotoTheme.SmallWithAvatar && (
        <FilesList>
          {filesArray.map((file: AddPhotoFile, index: number): JSX.Element => {
            const progressPercent: number | undefined = addPhotoState.fileProgressValues[file.id];

            return (
              <FileRowContainer>
                <FileRow
                  key={`${file.id}_${index}`}
                  onClick={(): void => {
                    setPhotoForPreview(file.path);
                    setIsFirstHover(false);
                  }}
                  onMouseEnter={(): void | false => index !== 0 && setIsFirstHover(false)}
                  onMouseLeave={(): void | false => (index !== 0 && !photoForPreview) && setIsFirstHover(true)}
                  $active={file.path === photoForPreview}
                  $isFirstHover={isFirstHover}
                >
                  <FileCell>
                    <Icon name={IconName.Photo} size={20} color={Color.Error500} />
                    <FileLabel>{t('onarte.ui.addPhoto.photo')} {index + 1}</FileLabel>
                  </FileCell>
                  {!progressPercent ? (
                    <RowButtonsContainer>
                      <ArrowsContainer>
                        <ArrowButton onClick={(): void => moveFileAction(index, DirectionEnum.Up)} disabled={!filesArray[index - 1]}>
                          <Icon name={IconName.ArrowUp15} size={16} color={!filesArray[index - 1] ? Color.Gray400 : Color.Error500} />
                        </ArrowButton>
                        <ArrowButton onClick={(): void => moveFileAction(index, DirectionEnum.Down)} disabled={!filesArray[index + 1]}>
                          <Icon name={IconName.ArrowDown15} size={16} color={!filesArray[index + 1] ? Color.Gray400 : Color.Error500} />
                        </ArrowButton>
                      </ArrowsContainer>
                      <FileRemoveButton onMouseDown={(e: SyntheticEvent): void => removeFileAction(file.id, e)}>
                        <Icon name={IconName.Delete} size={20} />
                      </FileRemoveButton>
                    </RowButtonsContainer>
                  ) : (
                    <ProgressBarContainer>
                      <ProgressBar $width={`${progressPercent}%`} />
                    </ProgressBarContainer>
                  )}
                </FileRow>
              </FileRowContainer>
            );
          })}
        </FilesList>
      )}
      <SizeErrorMessageContainer {...getCollapseProps()}>
        <SizeErrorMessage>
          {t('onarte.ui.addPhoto.sizeErrorMessage')}
        </SizeErrorMessage>
      </SizeErrorMessageContainer>
      {(
        filesArray.length < maxFilesByTheme
        && (
          (!filesArray.length && [AddPhotoTheme.WithBarLoaderOnInit, AddPhotoTheme.SmallWithAvatar].includes(addPhotoTheme))
          || !!filesArray.length
        )
      ) && (
        <AddPhotoBar onClick={hideSizeErrorMessage} $theme={addPhotoTheme}>
          <Icon name={IconName.AddLines} size={20} color={Color.Gray500} />
          <AddPhotoBarLabel>{t('onarte.ui.addPhoto.addPhoto')}</AddPhotoBarLabel>
          <FileInput type='file' onChange={onFileChange} accept={acceptedFilesTypes} disabled={isInputFileDisabled} />
        </AddPhotoBar>
      )}
      {!!(addPhotoTheme === AddPhotoTheme.SmallWithAvatar && filesArray.length) && (
        <ButtonsContainer>
          <AddPhotoBar onClick={hideSizeErrorMessage} $theme={addPhotoTheme}>
            <AddPhotoBarLabel>{t('onarte.ui.addPhoto.change')}</AddPhotoBarLabel>
            <FileInput type='file' onChange={onFileChange} accept={acceptedFilesTypes} disabled={isInputFileDisabled} />
          </AddPhotoBar>
          <AddPhotoBar onMouseDown={(e: SyntheticEvent): void => removeFileAction(filesArray[0].id, e)} $theme={addPhotoTheme}>
            <AddPhotoBarLabel>{t('onarte.ui.addPhoto.delete')}</AddPhotoBarLabel>
          </AddPhotoBar>
        </ButtonsContainer>
      )}
      {!!requirements.length && (
        <RequirementsBox>
          {requirements.map((requirement: string): JSX.Element => (
            <Requirement key={requirement}>
              <Dot />
              <RequirementLabel>{requirement}</RequirementLabel>
            </Requirement>
          ))}
        </RequirementsBox>
      )}
      {addPhotoTheme === AddPhotoTheme.SmallWithAvatar && !!validationMessage && (
        <StyledValidationBar message={validationMessage} barTheme={ValidationBarTheme.Information} />
      )}
    </Container>
  );
};
